// ============================================================
// CLM (Contract Lifecycle Management) — Shared Types
// Used across all 6 CLM prompts
// ============================================================

// ── Enums ────────────────────────────────────────────────────

export enum ContractType {
  FFP = 'FFP',
  CPFF = 'CPFF',
  T_AND_M = 'T_AND_M',
  IDIQ = 'IDIQ',
  GWAC = 'GWAC',
  BPA = 'BPA',
  BOA = 'BOA',
}

export enum TemplateStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  DEPRECATED = 'DEPRECATED',
}

export enum ClauseType {
  SOCIOECONOMIC = 'SOCIOECONOMIC',
  INTELLECTUAL_PROPERTY = 'INTELLECTUAL_PROPERTY',
  INSPECTION = 'INSPECTION',
  TERMINATION = 'TERMINATION',
  PAYMENT = 'PAYMENT',
  DISPUTES = 'DISPUTES',
  COMPLIANCE = 'COMPLIANCE',
  CYBERSECURITY = 'CYBERSECURITY',
  DATA_RIGHTS = 'DATA_RIGHTS',
  SMALL_BUSINESS = 'SMALL_BUSINESS',
  GENERAL = 'GENERAL',
  PRICING = 'PRICING',
  DELIVERABLES = 'DELIVERABLES',
  WARRANTY = 'WARRANTY',
  GOVERNMENT_PROPERTY = 'GOVERNMENT_PROPERTY',
}

export enum RiskLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

// ── Contract Type Display Helpers ────────────────────────────

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  [ContractType.FFP]: 'Firm Fixed Price',
  [ContractType.CPFF]: 'Cost Plus Fixed Fee',
  [ContractType.T_AND_M]: 'Time & Materials',
  [ContractType.IDIQ]: 'IDIQ',
  [ContractType.GWAC]: 'GWAC',
  [ContractType.BPA]: 'BPA',
  [ContractType.BOA]: 'BOA',
}

export const CONTRACT_TYPE_COLORS: Record<ContractType, string> = {
  [ContractType.FFP]: 'blue',
  [ContractType.CPFF]: 'blue',
  [ContractType.T_AND_M]: 'amber',
  [ContractType.IDIQ]: 'emerald',
  [ContractType.GWAC]: 'rose',
  [ContractType.BPA]: 'cyan',
  [ContractType.BOA]: 'orange',
}

export const RISK_LEVEL_COLORS: Record<RiskLevel, string> = {
  [RiskLevel.LOW]: 'emerald',
  [RiskLevel.MEDIUM]: 'amber',
  [RiskLevel.HIGH]: 'orange',
  [RiskLevel.CRITICAL]: 'red',
}

// ── Entity Interfaces ────────────────────────────────────────

export interface ContractTemplate {
  id: string
  name: string
  description: string
  contract_type: ContractType
  category: string
  version: number
  status: TemplateStatus
  created_by: string
  last_modified_by: string
  created_at: string
  updated_at: string
  usage_count: number
  avg_negotiation_days: number | null
  tags: string[]
  clauses: TemplateClause[]
}

export interface TemplateClause {
  id: string
  template_id: string
  clause_type: ClauseType
  clause_title: string
  clause_body: string
  is_required: boolean
  is_locked: boolean
  sort_order: number
  tags: string[]
  notes: string
  far_reference: string | null
  dfars_reference: string | null
  last_reviewed_at: string | null
  risk_level: RiskLevel
}

export interface ClauseLibraryItem {
  id: string
  clause_type: ClauseType
  clause_title: string
  clause_body: string
  regulatory_reference: string | null
  applicable_contract_types: ContractType[]
  tags: string[]
  is_active: boolean
  usage_count: number
  risk_level: RiskLevel
  jurisdiction: string | null
}

export interface TemplateVersion {
  id: string
  template_id: string
  version_number: number
  snapshot: ContractTemplate
  change_notes: string
  created_by: string
  created_at: string
}

// ── API Filter Types ─────────────────────────────────────────

export interface TemplateFilters {
  contract_type?: ContractType
  status?: TemplateStatus
  search?: string
}

export interface ClauseLibraryFilters {
  clause_type?: ClauseType
  search?: string
  contract_type?: ContractType
}

// ── Stats ────────────────────────────────────────────────────

export interface TemplateStats {
  usage_trend: { month: string; count: number }[]
  avg_days_to_close: number | null
  most_redlined_clauses: { clause_title: string; count: number }[]
  risk_distribution: { level: RiskLevel; count: number }[]
}
